import { z } from 'zod';
import { artifactUncertaintyOptionSchema, artifactReviewVerdictSchema, } from './artifact-claim-evidence.schema.js';
import { extractFrScAnchorsFromSpecMarkdown } from './major-acceptance-criteria.js';
export const remainderTestabilitySchema = z.enum([
    'in_repo_unit',
    'in_repo_integration',
    'doc_contract',
    'manual',
    'not_in_this_repo',
]);
export const specCurrentBehaviorEntrySchema = z
    .object({
    locator: z.string().min(1).max(1024),
    symbol: z.string().max(512).optional(),
    behavior_today: z.string().min(1).max(2048),
    evidence_id: z.string().min(1).max(128),
})
    .strict();
export const specRemainderRequirementEntrySchema = z
    .object({
    anchor: z.string().min(1).max(512),
    // Empty only for greenfield drafts; evaluateSpecDraftImplementability enforces ≥1 otherwise.
    depends_on_current: z.array(z.string().min(1).max(1024)).max(32),
    testability: remainderTestabilitySchema,
})
    .strict();
export const specDisagreementQuestionSchema = z
    .object({
    question_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
    text: z.string().min(1).max(2048),
    options: z.array(artifactUncertaintyOptionSchema).min(2).max(3),
    recommended_option_id: z
        .string()
        .min(1)
        .max(128)
        .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
})
    .strict()
    .superRefine((val, ctx) => {
    const ids = new Set();
    for (const [index, option] of val.options.entries()) {
        if (ids.has(option.option_id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'options[].option_id values must be unique',
                path: ['options', index, 'option_id'],
            });
        }
        ids.add(option.option_id);
    }
    if (!val.options.some((option) => option.option_id === val.recommended_option_id)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'recommended_option_id must match an options[].option_id',
            path: ['recommended_option_id'],
        });
    }
});
export const testPlanHarnessClassSchema = z.enum(['in_repo', 'doc_contract', 'manual_ops']);
export const changeMagnitudeSchema = z.enum(['TINY', 'SMALL', 'MEDIUM', 'LARGE']);
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function normalizeRelativePath(locator) {
    return locator
        .replace(/\\/g, '/')
        .replace(/^\.\//, '')
        .replace(/\/{2,}/g, '/')
        .trim();
}
function splitRepoQualifiedPath(locator) {
    const trimmed = locator.trim();
    const colon = trimmed.indexOf(':');
    if (colon > 0) {
        const prefix = trimmed.slice(0, colon);
        if (prefix.includes('/') &&
            !prefix.startsWith('/') &&
            !prefix.includes('://') &&
            !prefix.startsWith('.')) {
            return { path: normalizeRelativePath(trimmed.slice(colon + 1)) };
        }
    }
    return { path: normalizeRelativePath(trimmed) };
}
export function hasSpecRemainderContract(metadata) {
    if (!isRecord(metadata)) {
        return false;
    }
    return (Array.isArray(metadata['current_behavior']) && Array.isArray(metadata['remainder_requirements']));
}
export function parseSpecRemainderFromMetadata(metadata) {
    if (!hasSpecRemainderContract(metadata) || !isRecord(metadata)) {
        return null;
    }
    const behavior = z
        .array(specCurrentBehaviorEntrySchema)
        .max(256)
        .safeParse(metadata['current_behavior']);
    const remainder = z
        .array(specRemainderRequirementEntrySchema)
        .max(256)
        .safeParse(metadata['remainder_requirements']);
    if (!behavior.success || !remainder.success) {
        return null;
    }
    const questionsRaw = metadata['disagreement_questions'];
    const questionsParsed = Array.isArray(questionsRaw)
        ? z.array(specDisagreementQuestionSchema).max(6).safeParse(questionsRaw)
        : { success: true, data: [] };
    if (!questionsParsed.success) {
        return null;
    }
    return {
        current_behavior: behavior.data,
        remainder_requirements: remainder.data,
        disagreement_questions: questionsParsed.data,
    };
}
export function specRemainderFromAssistantPayload(payload) {
    if (!isRecord(payload)) {
        return null;
    }
    return payload['spec_agent_metadata'] ?? null;
}
function evidenceIdsFromMetadata(metadata) {
    const ids = new Set();
    if (!isRecord(metadata)) {
        return ids;
    }
    const evidenceBlock = metadata['artifact_claim_evidence'];
    if (!isRecord(evidenceBlock) || !Array.isArray(evidenceBlock['evidence'])) {
        return ids;
    }
    for (const row of evidenceBlock['evidence']) {
        if (!isRecord(row))
            continue;
        const id = row['evidence_id'];
        if (typeof id === 'string' && id.trim().length > 0) {
            ids.add(id.trim());
        }
    }
    return ids;
}
function normalizeLocatorKey(locator) {
    return splitRepoQualifiedPath(locator).path;
}
/** Maximums for a declared change magnitude. Null means that count is not capped. */
const SPEC_MAGNITUDE_CEILING = {
    TINY: {
        stories: 1,
        ac: 1,
        fr: 1,
        sc: 1,
        currentBehavior: 1,
        openQuestions: 1,
        forbidEdgeAndScope: true,
    },
    SMALL: {
        stories: 2,
        ac: 3,
        fr: 3,
        sc: 2,
        currentBehavior: 3,
        openQuestions: 2,
        forbidEdgeAndScope: false,
    },
    MEDIUM: {
        stories: 3,
        ac: null,
        fr: null,
        sc: null,
        currentBehavior: null,
        openQuestions: 4,
        forbidEdgeAndScope: false,
    },
    LARGE: {
        stories: null,
        ac: null,
        fr: null,
        sc: null,
        currentBehavior: null,
        openQuestions: 6,
        forbidEdgeAndScope: false,
    },
};
function countUserStoryHeadings(body) {
    let count = 0;
    for (const rawLine of body.split(/\r?\n/)) {
        if (/^###\s+User Story\s+\d+\b/i.test(rawLine.trim())) {
            count += 1;
        }
    }
    return count;
}
function countExplicitAnchors(body, kind) {
    const keys = new Set();
    const pattern = new RegExp(`\\b${kind}-(\\d{3})\\b`, 'g');
    for (const rawLine of body.split(/\r?\n/)) {
        pattern.lastIndex = 0;
        let match;
        while ((match = pattern.exec(rawLine)) !== null) {
            const num = match[1];
            if (num) {
                keys.add(`${kind}-${num}`);
            }
        }
    }
    return keys.size;
}
function markdownH2Titles(body) {
    const titles = [];
    for (const rawLine of body.split(/\r?\n/)) {
        const match = /^##\s+(.+?)\s*$/.exec(rawLine.trim());
        const title = match?.[1];
        if (title) {
            titles.push(title);
        }
    }
    return titles;
}
function countOptionedUncertainties(metadata) {
    const evidence = metadata['artifact_claim_evidence'];
    if (!isRecord(evidence)) {
        return 0;
    }
    const uncertainties = evidence['uncertainties'];
    if (!Array.isArray(uncertainties)) {
        return 0;
    }
    let count = 0;
    for (const row of uncertainties) {
        if (!isRecord(row)) {
            continue;
        }
        const options = row['options'];
        if (Array.isArray(options) && options.length >= 2) {
            count += 1;
        }
    }
    return count;
}
function pushIfOver(violations, label, actual, max) {
    if (max !== null && actual > max) {
        violations.push(`${label}:${actual}>${max}`);
    }
}
function evaluateSpecMagnitudeCeiling(input) {
    const declared = extractDeclaredChangeMagnitude(input.bodyMarkdown);
    if (declared === null) {
        return { ok: false, code: 'spec_magnitude_missing', detail: 'missing' };
    }
    const ceiling = SPEC_MAGNITUDE_CEILING[declared];
    const violations = [];
    pushIfOver(violations, 'stories', countUserStoryHeadings(input.bodyMarkdown), ceiling.stories);
    pushIfOver(violations, 'ac', countExplicitAnchors(input.bodyMarkdown, 'AC'), ceiling.ac);
    pushIfOver(violations, 'fr', countExplicitAnchors(input.bodyMarkdown, 'FR'), ceiling.fr);
    pushIfOver(violations, 'sc', countExplicitAnchors(input.bodyMarkdown, 'SC'), ceiling.sc);
    pushIfOver(violations, 'current_behavior', input.currentBehaviorCount, ceiling.currentBehavior);
    pushIfOver(violations, 'open_questions', countOptionedUncertainties(input.metadata), ceiling.openQuestions);
    if (ceiling.forbidEdgeAndScope) {
        for (const title of markdownH2Titles(input.bodyMarkdown)) {
            if (/^edge cases$/i.test(title)) {
                violations.push('heading:Edge Cases');
            }
            if (/^out of scope$/i.test(title)) {
                violations.push('heading:Out of Scope');
            }
        }
    }
    if (violations.length > 0) {
        return {
            ok: false,
            code: 'spec_magnitude_over_ceiling',
            detail: `${declared} ${violations.join(',')}`,
        };
    }
    return { ok: true };
}
export function evaluateSpecDraftImplementability(input) {
    if (!isRecord(input.metadata)) {
        return { ok: true, skipped: true };
    }
    const hasBehavior = input.metadata['current_behavior'] !== undefined;
    const hasRemainder = input.metadata['remainder_requirements'] !== undefined;
    if (!hasBehavior && !hasRemainder) {
        if (input.cloneAvailable === true) {
            return {
                ok: false,
                code: 'remainder_fields_incomplete',
                detail: 'current_behavior,remainder_requirements',
            };
        }
        return { ok: true, skipped: true };
    }
    if (hasBehavior !== hasRemainder) {
        return {
            ok: false,
            code: 'remainder_fields_incomplete',
            detail: 'current_behavior,remainder_requirements',
        };
    }
    const parsed = parseSpecRemainderFromMetadata(input.metadata);
    if (!parsed) {
        const questionsRaw = input.metadata['disagreement_questions'];
        if (Array.isArray(questionsRaw) && questionsRaw.length > 0) {
            return { ok: false, code: 'disagreement_unoptioned', detail: 'disagreement_questions' };
        }
        return { ok: false, code: 'remainder_fields_incomplete', detail: 'spec_remainder_unparseable' };
    }
    const markdownAnchors = extractFrScAnchorsFromSpecMarkdown(input.bodyMarkdown);
    const remainderAnchors = parsed.remainder_requirements
        .map((row) => row.anchor)
        .filter((anchor) => /^(FR|SC)-\d{3}$/.test(anchor));
    const remainderSet = new Set(remainderAnchors);
    const missingInRemainder = markdownAnchors.filter((anchor) => !remainderSet.has(anchor));
    const extraInRemainder = remainderAnchors.filter((anchor) => !markdownAnchors.includes(anchor));
    if (missingInRemainder.length > 0 || extraInRemainder.length > 0) {
        return {
            ok: false,
            code: 'remainder_anchor_identity_mismatch',
            detail: [...missingInRemainder, ...extraInRemainder].slice(0, 16).join(','),
        };
    }
    const behaviorLocators = new Set(parsed.current_behavior.map((row) => normalizeLocatorKey(row.locator)).filter(Boolean));
    const evidenceIds = evidenceIdsFromMetadata(input.metadata);
    for (const row of parsed.current_behavior) {
        if (!evidenceIds.has(row.evidence_id)) {
            return { ok: false, code: 'current_behavior_evidence_missing', detail: row.evidence_id };
        }
    }
    if (parsed.current_behavior.length > 0) {
        for (const row of parsed.remainder_requirements) {
            const okDep = row.depends_on_current.some((locator) => behaviorLocators.has(normalizeLocatorKey(locator)));
            if (!okDep) {
                return { ok: false, code: 'remainder_depends_on_missing', detail: row.anchor };
            }
        }
    }
    const grounded = new Set((input.groundedLocators === undefined
        ? []
        : input.groundedLocators instanceof Set
            ? [...input.groundedLocators]
            : [...input.groundedLocators]).map((locator) => normalizeLocatorKey(locator)));
    if (input.cloneAvailable === true) {
        for (const row of parsed.current_behavior) {
            const key = normalizeLocatorKey(row.locator);
            if (input.pathExists !== undefined &&
                !input.pathExists(key) &&
                !input.pathExists(row.locator)) {
                return { ok: false, code: 'current_behavior_locator_missing', detail: row.locator };
            }
            if (!grounded.has(key)) {
                return { ok: false, code: 'current_behavior_ungrounded', detail: row.locator };
            }
        }
    }
    for (const question of parsed.disagreement_questions) {
        if (question.options.length < 2 || question.options.length > 3) {
            return { ok: false, code: 'disagreement_unoptioned', detail: question.question_id };
        }
    }
    return evaluateSpecMagnitudeCeiling({
        bodyMarkdown: input.bodyMarkdown,
        metadata: input.metadata,
        currentBehaviorCount: parsed.current_behavior.length,
    });
}
export function specHasDisagreementHold(metadata) {
    const parsed = parseSpecRemainderFromMetadata(metadata);
    return (parsed?.disagreement_questions.length ?? 0) > 0;
}
export function disagreementHoldReadiness(questions) {
    return {
        outcome: 'clarification_required',
        questions: questions.slice(0, 3),
        rubric_outcome: {
            bucket: 'underspecified_intent',
            title_word_count: 0,
            has_description: true,
            has_acceptance_criteria: true,
        },
    };
}
/** Stable episode question id for the disagreement question at `index` (0-based, max 3). */
export function specDisagreementClarificationQuestionId(index) {
    const n = (index + 1).toString().padStart(12, '0');
    return `00000000-0000-4000-8000-${n}`;
}
/**
 * Episode ids for the disagreement questions that open a clarification pause.
 * The same ids are stamped onto the kept uncertainties at draft enrich time.
 */
export function listDisagreementClarificationLinks(metadata) {
    const parsed = parseSpecRemainderFromMetadata(metadata);
    const questions = parsed?.disagreement_questions ?? [];
    return questions.slice(0, 3).map((question, index) => ({
        questionId: question.question_id,
        text: question.text,
        clarificationQuestionId: specDisagreementClarificationQuestionId(index),
    }));
}
export function disagreementQuestionsToClarificationQuestions(metadata) {
    const parsed = parseSpecRemainderFromMetadata(metadata);
    const questions = parsed?.disagreement_questions ?? [];
    return questions.slice(0, 3).map((question, index) => {
        const options = question.options.map((option) => `- ${option.label}`).join('\n');
        return {
            id: specDisagreementClarificationQuestionId(index),
            prompt_markdown: `${question.text}\n\n${options}`,
        };
    });
}
/** Persistence paths: a plan touching one is LARGE regardless of the declared size. */
const PERSISTENCE_PATH = /(migration|\.sql$|drizzle\/)/i;
/** Paths that can justify LARGE (API, queue, flag); too fuzzy (e.g. UI `routes/`) to force it. */
const LARGE_SIGNAL_PATH = /(feature.?gate|bullmq|queue|kafka|pubsub|openapi|http\.ts|routes\/|router\.ts|controller\.ts)/i;
export function allowlistMagnitudeSignals(allowlist) {
    const rows = allowlist ?? [];
    const touchesPersistence = rows.some((row) => PERSISTENCE_PATH.test(row.path));
    const repoKeys = new Set(rows.map((row) => row.repo_key.trim().toLowerCase()));
    return {
        fileCount: new Set(rows.map((row) => `${row.repo_key}:${normalizeRelativePath(row.path)}`))
            .size,
        touchesPersistence,
        supportsLarge: touchesPersistence ||
            repoKeys.size > 1 ||
            rows.some((row) => LARGE_SIGNAL_PATH.test(row.path)),
    };
}
/** Smallest magnitude the allowlist alone can justify (LOC is not visible, so TINY is a floor). */
export function deriveChangeMagnitudeFromAllowlist(allowlist) {
    const signals = allowlistMagnitudeSignals(allowlist);
    if (signals.touchesPersistence) {
        return 'LARGE';
    }
    if (signals.fileCount >= 4) {
        return 'MEDIUM';
    }
    if (signals.fileCount >= 2) {
        return 'SMALL';
    }
    return 'TINY';
}
export function extractDeclaredChangeMagnitude(text) {
    const match = /\bchange magnitude\b[^A-Z]*(TINY|SMALL|MEDIUM|LARGE)\b/i.exec(text);
    if (!match?.[1]) {
        return null;
    }
    const value = match[1].toUpperCase();
    if (value === 'TINY' || value === 'SMALL' || value === 'MEDIUM' || value === 'LARGE') {
        return value;
    }
    return null;
}
export function evaluateTechPlanMagnitude(input) {
    const declared = extractDeclaredChangeMagnitude(input.declaredText);
    const signals = allowlistMagnitudeSignals(input.allowlist);
    if (declared === null || signals.fileCount === 0) {
        return { ok: true };
    }
    const mismatch = declared === 'LARGE'
        ? !signals.supportsLarge
        : signals.touchesPersistence ||
            (declared === 'TINY' && signals.fileCount > 1) ||
            (declared === 'SMALL' && signals.fileCount > 3);
    if (!mismatch) {
        return { ok: true };
    }
    const derived = deriveChangeMagnitudeFromAllowlist(input.allowlist);
    return { ok: false, code: 'magnitude_mismatch', detail: `${declared}->${derived}` };
}
export function evaluateAlreadySatisfiedCitesCurrentBehavior(input) {
    if (!input.specRemainder || input.specRemainder.current_behavior.length === 0) {
        return true;
    }
    const notes = input.notes?.trim() ?? '';
    if (notes.length === 0) {
        return false;
    }
    return input.specRemainder.current_behavior.some((row) => {
        const path = normalizeLocatorKey(row.locator);
        return path.length > 0 && notes.includes(path);
    });
}
export function evaluateTestPlanRemainderCoverage(input) {
    if (!input.specRemainder) {
        return { ok: true, skipped: true };
    }
    const coverage = input.majorAcCoverage ?? [];
    const mapped = new Set(coverage.filter((row) => row.disposition === 'scenario_mapped').map((row) => row.ac_anchor));
    const satisfied = new Set((input.planMappings ?? [])
        .filter((row) => row.disposition === 'already_satisfied')
        .map((row) => row.requirement_anchor));
    for (const anchor of satisfied) {
        if (mapped.has(anchor)) {
            return { ok: false, code: 'satisfied_has_scenario', detail: anchor };
        }
    }
    for (const row of input.specRemainder.remainder_requirements) {
        if (row.testability.startsWith('in_repo_') &&
            !mapped.has(row.anchor) &&
            !satisfied.has(row.anchor)) {
            return { ok: false, code: 'remainder_untested', detail: row.anchor };
        }
    }
    for (const scenario of input.scenarios ?? []) {
        const harness = scenario.harness_class?.trim();
        if (harness && !testPlanHarnessClassSchema.safeParse(harness).success) {
            return { ok: false, code: 'harness_class_invalid', detail: harness };
        }
    }
    return { ok: true };
}
function emptyStrip() {
    return {
        currentBehaviorCount: 0,
        remainderCount: 0,
        alreadySatisfiedCount: 0,
        addressedCount: 0,
        clarificationRequiredCount: 0,
        inRepoCount: 0,
        notInRepoCount: 0,
        implementationTaskCount: 0,
        deferralTaskCount: 0,
        reviewVerdict: null,
        requiredRevisionSummary: null,
    };
}
function reviewFromMetadata(metadata) {
    if (!isRecord(metadata) || metadata['artifact_review'] === undefined) {
        return { reviewVerdict: null, requiredRevisionSummary: null };
    }
    const parsed = artifactReviewVerdictSchema.safeParse(metadata['artifact_review']);
    if (!parsed.success) {
        return { reviewVerdict: null, requiredRevisionSummary: null };
    }
    return {
        reviewVerdict: parsed.data.verdict,
        requiredRevisionSummary: parsed.data.required_revision_summary ?? null,
    };
}
export function buildRemainderApprovalStrip(input) {
    const strip = emptyStrip();
    const spec = parseSpecRemainderFromMetadata(input.specMetadata);
    if (spec) {
        strip.currentBehaviorCount = spec.current_behavior.length;
        strip.remainderCount = spec.remainder_requirements.length;
        strip.inRepoCount = spec.remainder_requirements.filter((row) => row.testability.startsWith('in_repo_')).length;
        strip.notInRepoCount = spec.remainder_requirements.filter((row) => row.testability === 'not_in_this_repo').length;
    }
    if (isRecord(input.techPlanMetadata) &&
        Array.isArray(input.techPlanMetadata['requirement_mappings'])) {
        for (const row of input.techPlanMetadata['requirement_mappings']) {
            if (!isRecord(row) || typeof row['disposition'] !== 'string')
                continue;
            if (row['disposition'] === 'already_satisfied')
                strip.alreadySatisfiedCount += 1;
            if (row['disposition'] === 'addressed')
                strip.addressedCount += 1;
            if (row['disposition'] === 'clarification_required')
                strip.clarificationRequiredCount += 1;
        }
    }
    for (const task of input.tasks ?? []) {
        if (task.task_kind === 'implementation' || task.task_kind === 'repo_validation') {
            strip.implementationTaskCount += 1;
        }
        if (task.task_kind === 'coverage_deferral') {
            strip.deferralTaskCount += 1;
        }
    }
    const reviewMeta = (isRecord(input.specMetadata) && input.specMetadata['artifact_review'] !== undefined
        ? input.specMetadata
        : null) ??
        (isRecord(input.techPlanMetadata) && input.techPlanMetadata['artifact_review'] !== undefined
            ? input.techPlanMetadata
            : null) ??
        (isRecord(input.testPlanMetadata) && input.testPlanMetadata['artifact_review'] !== undefined
            ? input.testPlanMetadata
            : null);
    const review = reviewFromMetadata(reviewMeta);
    strip.reviewVerdict = review.reviewVerdict;
    strip.requiredRevisionSummary = review.requiredRevisionSummary;
    return strip;
}
export function remainderContractJson(input) {
    return JSON.stringify({
        current_behavior: input.current_behavior ?? [],
        remainder_requirements: input.remainder_requirements ?? [],
        requirement_mappings: input.requirement_mappings ?? [],
        affected_component_allowlist: input.affected_component_allowlist ?? [],
        selected_repo_full_names: input.selected_repo_full_names ?? [],
        already_shipped: {
            specification_version_id: input.previous_specification_version_id ?? null,
            technical_plan_version_id: input.previous_technical_plan_version_id ?? null,
        },
    }, null, 2);
}
//# sourceMappingURL=spec-remainder-contract.js.map